import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  ArrowLeft,
  ArrowRight,
  FileText,
  FileSignature,
  LifeBuoy,
  Pencil,
  Plus,
  Receipt,
  StickyNote,
  Trash2,
} from 'lucide-react';
import {
  ArchivedToggle,
  Button,
  buttonVariants,
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  InfoHint,
  ProgressBar,
  Skeleton,
  StatCard,
  StatusPill,
  Tabs,
  invoiceStatusToPill,
  withReturn,
  type DataTableColumn,
} from '../../components/ui';
import { formatDate, formatHours, formatMoney, formatMonth, formatPercent } from '../../lib/format';
import { cn } from '../../lib/cn';
import { ApiError } from '../../lib/api';
import { MarginText, VerdictChip } from '../agreements/components';
import {
  AgreementMobileCard,
  AgreementsTable,
  AgreementsTotalsCard,
  rollupAgreements,
  type AgreementTableRow,
} from '../agreements/AgreementsTable';
// Support on this page is the SAME bank /support renders, through the SAME
// components — the blocks/statement panel and the balance chrome are imported,
// never re-implemented, exactly as the agreements table is.
import { AddHoursModal } from '../support/AddHoursModal';
import { BankPanel } from '../support/BankPanel';
import { BalanceBar, BankStatusPill, relativeDays } from '../support/components';
import type { SupportBankRow, UnbankedSale } from '../support/api';
import { UnbankedSales } from '../support/UnbankedSales';
import { UnpaidBankHours } from '../support/UnpaidBankHours';
import { DeleteClientModal } from './DeleteClientModal';
import {
  useClient,
  useClientDeletion,
  useUpdateClient,
  type ClientDetail,
  type ClientInvoice,
  type ClientSupport,
  type SupportMonth,
} from './api';
import { clientActions, clientVerdict } from './verdict';
import { EXPIRY_HORIZON_DAYS } from './support-signal';
// One sentence about which revenue this is, shared with /clients so the list and
// the page it opens cannot drift apart again.
import {
  REVENUE_BASIS_NOTE_SHORT,
  RevenueBasisInfo,
} from './revenue-basis';
// The one basis-aware, bank-aware definition of "unbilled" — the words and the
// link, shared with /clients so the list and this page cannot disagree.
import {
  PREPAID_TITLE,
  UNBILLED_SPLIT_TITLE,
  UNBILLED_TITLE,
  unbilledSplit,
} from './unbilled';
import { ClientHubOrg } from './ClientHubOrg';
import { ClientFormModal } from './components/ClientFormModal';
import { SendTopUpLink } from './components/SendTopUpLink';

// ⚠ THIS TILE USED TO BE A GENERATION BEHIND `/clients`.
// It was headed "Revenue earned" — the accrual verb — over a figure that blends
// three bases, with a tooltip that mentioned neither cash nor the P&L, while
// the list one click away already said "It values the WORK, not cash received".
// One shared sentence now serves both (`revenue-basis.ts`), and the page states
// it in PROSE under the strip as well, because a tooltip is not readable on a
// phone. The hint's own basis (invoiced, accrual, by issue date) is named too:
// three different readings sat in one tile with one word above them.
/** The clause this TILE adds to the shared revenue-basis explanation. */
const REVENUE_EARNED_NOTE =
  'The hint under the figure is a fourth reading again — what has actually been INVOICED to date (accrual, by issue date) — which differs from the figure above by unbilled time and by work billed ahead of delivery, and from cash by whatever is still unpaid.';

export function ClientDetailPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { data: client, isLoading, error, refetch } = useClient(clientId);
  const updateClient = useUpdateClient(clientId ?? '');
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Asked only once the client is archived — deletion lives on the far side
  // of that reversible step, and the SERVER says whether this viewer may.
  const deletion = useClientDeletion(clientId, client ? !client.isActive : false);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab = ['agreements', 'invoices', 'support', 'notes'].includes(tabParam ?? '')
    ? tabParam!
    : 'agreements';
  // Archived agreements are hidden until asked for — the same counted toggle,
  // and the same `?archived=1`, as /agreements. Jesse, 2026-10-06: "clients
  // should not show archived agreements by default... just open".
  const showArchived = searchParams.get('archived') === '1';
  const setShowArchived = (next: boolean) => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set('archived', '1');
    else params.delete('archived');
    setSearchParams(params, { replace: true });
  };

  if (isLoading) return <DetailSkeleton />;

  if (error || !client) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div>
        <Card>
          <EmptyState
            icon={<AlertTriangle />}
            title={notFound ? 'Client not found' : "Couldn't load this client"}
            description={
              notFound
                ? 'It may have been deleted, or the link is wrong.'
                : 'Something went wrong talking to the server.'
            }
            action={
              notFound ? (
                <Button variant="outline" onClick={() => navigate('/clients')}>
                  <ArrowLeft className="h-4 w-4" aria-hidden />
                  Back to clients
                </Button>
              ) : (
                <Button variant="outline" onClick={() => refetch()}>
                  Try again
                </Button>
              )
            }
          />
        </Card>
      </div>
    );
  }

  const { stats, rollup } = client;
  const verdict = clientVerdict(stats, rollup, client.agreements.length, client.support);
  // Up to two: what to do about money owed, and what to do about work
  // delivered. Neither is allowed to take the other's slot — see `clientActions`.
  const actions = clientActions(client.id, stats, rollup, client.support);

  return (
    <div>
      <Link
        to="/clients"
        className="mb-3 inline-flex items-center gap-1.5 text-body-sm font-semibold text-grey-500 transition-colors hover:text-secondary"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Clients
      </Link>

      <div className="mb-4 flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-heading-2 text-secondary">{client.name}</h1>
            {/* The verdict, next to the name, before any tile — exactly as the
                agreement page does it. Everything below is evidence; this is the
                answer. */}
            <VerdictChip tone={verdict.tone} label={verdict.label} title={verdict.reason} />
            {!client.isActive && <StatusPill status="neutral" label="Archived" dot={false} />}
          </div>
          <p className="mt-1 text-body-sm text-grey-500">
            {stats.activeAgreements > 0
              ? `${stats.activeAgreements} active agreement${stats.activeAgreements === 1 ? '' : 's'}`
              : 'No active agreements'}
            {stats.totalAgreements > stats.activeAgreements
              ? ` · ${stats.totalAgreements - stats.activeAgreements} archived`
              : ''}
            {rollup.all.hours > 0 ? ` · ${formatHours(rollup.all.hours, { unit: true })} tracked` : ''}
            {` · ${stats.invoiceCount} invoice${stats.invoiceCount === 1 ? '' : 's'}`}
            {stats.avgDaysToPay !== null ? ` · pays in ~${Math.round(stats.avgDaysToPay)} days` : ''}
          </p>
          <p className="mt-1.5 max-w-2xl text-body-sm font-semibold text-secondary">{verdict.reason}</p>
          {/* The hub/accounts organization behind this client, when one is on
              record — the mapping that decides where imported work lands. */}
          <ClientHubOrg hubOrgId={client.hubOrgId} />
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          {/* The one thing to do next. A page that says "$24,360 overdue" and
              offers no way to act on it is a report, not a tool. */}
          {actions.map((action, i) => (
            <Link
              key={action.to}
              to={action.to}
              title={action.title}
              className={buttonVariants({
                size: 'sm',
                // One PRIMARY action, still. The second is the other fact's
                // way in, not a second shout.
                variant: i === 0 ? 'primary' : 'outline',
                className: 'w-full sm:w-auto',
              })}
            >
              <action.icon className="h-4 w-4" aria-hidden />
              {action.label}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          ))}
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="h-4 w-4" aria-hidden />
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            loading={updateClient.isPending}
            onClick={() => updateClient.mutate({ isActive: !client.isActive })}
          >
            {client.isActive ? (
              <>
                <Archive className="h-4 w-4" aria-hidden />
                Archive
              </>
            ) : (
              <>
                <ArchiveRestore className="h-4 w-4" aria-hidden />
                Restore
              </>
            )}
          </Button>
          {deletion.data?.canDelete && deletion.data.allowed && (
            <Button variant="ghost" size="sm" className="text-error" onClick={() => setDeleteOpen(true)}>
              <Trash2 className="h-4 w-4" aria-hidden />
              Delete
            </Button>
          )}
        </div>
      </div>
      {deletion.data?.allowed && (
        <DeleteClientModal
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          preview={deletion.data}
          onDeleted={() => navigate('/clients?status=archived', { replace: true })}
        />
      )}

      {/* Revenue, what it COST to earn, the margin between them, what has been
          delivered and not billed, and what has been billed and not paid. The
          four tiles used to be revenue-and-AR only — the whole cost/margin
          differentiator evaporated the moment you left /agreements.
          Whole dollars throughout: this row is a rollup, and a rollup that
          mixes $289,320 with $48,680.00 is two precisions in one glance.

          ⚠ THE LAST TWO TILES ARE TWO DIFFERENT FACTS AND BOTH ALWAYS RENDER.
          Unbilled is work done and not billed; Open AR is work billed and not
          paid. The strip carried only the second, and the hero button chased
          only the second, so a client with both had no unbilled figure anywhere
          on the page. Left to right the money reads the way it does in life:
          delivered → unbilled → open → overdue. */}
      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-3 lg:gap-4 xl:grid-cols-5">
        {/* Revenue as the agreements engine defines it — billable value on hourly
            work, invoiced on fee work — because that is the denominator of the
            margin two tiles along, and the same number the clients list and the
            agreements table sum. What has actually been billed rides in the hint. */}
        {/* "Lifetime revenue" named neither basis: the figure is agreement-
            accrued revenue and the hint is what was invoiced. The label now
            says which one it is, and the tooltip says how the two differ. */}
        <StatCard
          // "Revenue earned" was the accrual verb over a blend of contract
          // value, hours × rate and invoiced — and the word "earned" is exactly
          // what the firm's cash-basis revenue is NOT. The label matches the
          // list's column head word for word, the SHORT qualifier rides in the
          // hint below, and the basis paragraph is behind the (i).
          label={
            <>
              {/* The (i) rides with the last word: alone on a wrapped line in a
                  five-tile strip it reads as a stray mark, not an affordance. */}
              Revenue · all{' '}
              <span className="whitespace-nowrap">
                time
                <InfoHint label="Revenue basis" width={320}>
                  <RevenueBasisInfo />
                  <p className="mt-2">{REVENUE_EARNED_NOTE}</p>
                </InfoHint>
              </span>
            </>
          }
          kind="revenue"
          value={formatMoney(rollup.client.revenue, { whole: true })}
          // Lifetime AND this year. The page had no period scope at all, so a
          // relationship that ended two years ago read exactly like one billing
          // hard now; `ytdRevenue` was in the payload and rendered nowhere.
          // ⚠ TWO LINES IS THE WHOLE BUDGET (`StatCard` clamps the hint), and
          // at 390px a half-width tile fits about 17 characters a line. The
          // words "to date" cost eight of the 34 and bought nothing the label
          // above does not already say, so this hint clipped mid-figure —
          // "$427,278 invoiced to date · $43,129…" — on the one screen where a
          // tooltip cannot rescue it. The long form stays as the title.
          hint={
            <span
              title={
                stats.ytdRevenue > 0
                  ? `${formatMoney(stats.lifetimeRevenue, { whole: true })} invoiced to date, of which ${formatMoney(stats.ytdRevenue, { whole: true })} in ${new Date().getFullYear()}`
                  : `${formatMoney(stats.lifetimeRevenue, { whole: true })} invoiced to date, none of it in ${new Date().getFullYear()}`
              }
            >
              {formatMoney(stats.lifetimeRevenue, { whole: true })} invoiced ·{' '}
              {stats.ytdRevenue > 0
                ? `${formatMoney(stats.ytdRevenue, { whole: true })} in ${new Date().getFullYear()}`
                : `nothing in ${new Date().getFullYear()}`}
            </span>
          }
          hintLines={2}
        />
        <StatCard
          label="Delivery cost"
          kind="cost"
          value={formatMoney(rollup.all.cost, { whole: true })}
          hint={
            rollup.internal.cost > 0
              ? `incl. ${formatMoney(rollup.internal.cost, { whole: true })} non-billable`
              : `${formatHours(rollup.all.hours, { unit: true })} at cost rates + expenses`
          }
          hintLines={2}
        />
        <StatCard
          label="Blended margin"
          kind="margin"
          value={<MarginText margin={rollup.client.margin} size="heading" />}
          hint={
            rollup.client.margin == null
              ? 'no revenue recognised yet'
              : `on ${formatMoney(rollup.client.revenue, { whole: true })} of agreement revenue`
          }
          hintLines={2}
        />
        {/* Delivered, and nobody has been asked to pay for it. The figure is
            `rollup.unbilled` — the SAME server-computed, basis-aware,
            bank-aware number the /clients column prints and the invoice wizard
            offers to the cent. The tile IS the way to act on it: it opens the
            wizard on the span that holds the money, so seeing it and billing it
            is one click, not a hunt through /invoices/new's client picker. */}
        <StatCard
          label={<span title={UNBILLED_TITLE}>Unbilled</span>}
          kind="unbilled"
          value={formatMoney(rollup.unbilled, { whole: true })}
          valueClassName={rollup.unbilled > 0 ? 'text-accent' : undefined}
          // No wizard link (Jesse, 2026-08-22): hourly invoices are raised by
          // amount, never by gathering hours — the figure informs, it is not
          // an errand. The support-overage button below keeps its link.
          hint={
            rollup.unbilled > 0 ? (
              <span
                title={
                  `Invoice ${formatMoney(rollup.unbilled, { whole: true })} of unbilled work for ${client.name}` +
                  (stats.unbilledFrom ? ` — delivered since ${formatDate(stats.unbilledFrom)}` : '') +
                  (rollup.unbilledExpenses > 0
                    ? `, including ${formatMoney(rollup.unbilledExpenses, { whole: true })} of billable expenses`
                    : '') +
                  (stats.unbilledPrepaid > 0
                    ? `. A further ${formatHours(stats.unbilledPrepaidHours)} h (${formatMoney(
                        stats.unbilledPrepaid,
                        { whole: true },
                      )}) of uninvoiced support is already paid for out of their prepaid block — it is excluded here and must not be billed again.`
                    : '.')
                }
              >
                {stats.unbilledFrom ? `oldest ${formatDate(stats.unbilledFrom)}` : null}
                {/* ⚠ The prepaid line is what stops someone billing an hour
                    twice, so it rides the tile as well as the list. */}
                {stats.unbilledPrepaid > 0 ? (
                  <span className="text-grey-500" title={PREPAID_TITLE}>
                    {' '}
                    · {formatHours(stats.unbilledPrepaidHours, { fixed: true })} h prepaid, excluded
                  </span>
                ) : null}
              </span>
            ) : stats.unbilledPrepaid > 0 ? (
              <span title={PREPAID_TITLE}>
                {formatHours(stats.unbilledPrepaidHours, { fixed: true })} h prepaid, already paid for
              </span>
            ) : (
              'everything delivered is invoiced'
            )
          }
          /* ⚠ BOTH HALVES, AS AN ADDITION, ON ITS OWN LINE.
             The tile named only the expense half and left the time half to be
             worked out by subtraction — which is how "17 h beyond the bank" in
             the paragraph above and "$2,985 unbilled" here came to read as two
             prices for one set of hours. It goes in `footer`, not the hint: the
             hint is `line-clamp-2` and a clipped reconciliation ("$2,805 time ·
             $180…") is worse than none. */
          footer={
            unbilledSplit(rollup.unbilled, rollup.unbilledExpenses) ? (
              <span className="num block text-caption text-grey-500" title={UNBILLED_SPLIT_TITLE}>
                {unbilledSplit(rollup.unbilled, rollup.unbilledExpenses)}
              </span>
            ) : undefined
          }
          hintLines={2}
        />
        <StatCard
          label="Open AR"
          kind="ar"
          value={formatMoney(stats.openAr, { whole: true })}
          // The other half of the pair, and it is actionable for the same
          // reason: overdue money leads straight to the late invoices behind it.
          to={
            stats.openAr > 0
              ? `/invoices?${stats.overdueAr > 0 ? 'state=LATE&' : ''}client=${encodeURIComponent(client.id)}`
              : undefined
          }
          hint={
            stats.overdueAr > 0 ? (
              <span className="num font-semibold text-primary-600">
                {formatMoney(stats.overdueAr, { whole: true })} overdue across {stats.overdueCount}{' '}
                invoice{stats.overdueCount === 1 ? '' : 's'}
              </span>
            ) : stats.openAr > 0 ? (
              'nothing overdue'
            ) : (
              'nothing outstanding'
            )
          }
          hintLines={2}
        />
      </div>

      {/* ⚠ A THREE-LINE BASIS PARAGRAPH USED TO SIT HERE (Jesse, 2026-08-13:
          *"don't need explanations on clients view"*). What survives is the
          SHORT qualifier — the words that stop a reader taking this strip's
          money for the firm's cash revenue — and the explanation is one click
          away on the Revenue tile's (i). Do not restore the paragraph, and do
          not delete this line: the figure above it is genuinely ambiguous. */}
      <p className="mb-5 -mt-1 text-caption text-grey-400">
        Revenue · {REVENUE_BASIS_NOTE_SHORT}
      </p>

      <Tabs
        // Narrow screens: let the tab strip scroll instead of wrapping labels
        // onto two lines (same treatment as the agreement page).
        className="[&>[role=tablist]]:flex-nowrap [&>[role=tablist]]:overflow-x-auto [&_[role=tab]]:whitespace-nowrap"
        value={tab}
        onValueChange={(value) => {
          const params = new URLSearchParams(searchParams);
          if (value === 'agreements') params.delete('tab');
          else params.set('tab', value);
          setSearchParams(params, { replace: true });
        }}
        items={[
          {
            value: 'agreements',
            // The count is the rows the tab shows — open ones unless archived
            // are switched on.
            label: `Agreements (${
              showArchived
                ? client.agreements.length
                : client.agreements.filter((p) => p.isActive).length
            })`,
            content: (
              <AgreementsTab
                client={client}
                showArchived={showArchived}
                onShowArchived={setShowArchived}
              />
            ),
          },
          {
            value: 'invoices',
            // The COUNT, not the page size. This label printed
            // `invoices.length` — always 12 — under a header saying "17
            // invoices". A number in a label has to be the number.
            label: `Invoices (${stats.invoiceCount})`,
            content: <InvoicesTab client={client} />,
          },
          {
            value: 'support',
            label: supportTabLabel(client.support),
            content: <SupportTab client={client} />,
          },
          {
            value: 'notes',
            label: 'Notes',
            content: <NotesTab client={client} onEdit={() => setEditOpen(true)} />,
          },
        ]}
      />

      <ClientFormModal
        open={editOpen}
        onOpenChange={setEditOpen}
        title="Edit client"
        submitLabel="Save changes"
        initial={{ name: client.name, notes: client.notes ?? '', billing: client.billing }}
        submitting={updateClient.isPending}
        onSubmit={(values) =>
          updateClient.mutateAsync({
            name: values.name,
            notes: values.notes || null,
            ...values.billing,
          })
        }
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Agreements tab
// ---------------------------------------------------------------------------

/**
 * Every agreement and invoice row on a CLIENT page carries `?from=client`, so
 * the page it opens sends the reader back HERE. Jesse, 2026-09-10: *"if I
 * select a client I want to stay in that context… and not be taken back to a
 * list of invoices."* Billing one client is a loop through its agreements and
 * its invoices, and every hop used to eject you into the portfolio.
 */
const agreementHref = (p: AgreementTableRow) => withReturn(`/agreements/${p.id}`, 'client');

/**
 * "New invoice" for THIS client — the invoice builder with the client already
 * chosen (on Prepaid hours, its first tab; the client carries across every
 * tab), and `?from=client` so the draft it creates leads back here.
 *
 * The Invoices tab used to offer this only in its empty state, and that button
 * pointed at `/invoices` — the portfolio, not the builder — so a client with
 * one invoice had no way to raise a second without going out to /invoices/new
 * and finding them again in the picker.
 */
const newInvoiceHref = (clientId: string) =>
  withReturn(`/invoices/new?clientId=${encodeURIComponent(clientId)}`, 'client');

/**
 * The client's agreements, in the SAME instrument as /agreements.
 *
 * This was a 2-up card grid with Hours / Revenue / Margin and no cost, no
 * totals and ~450px of dead space under two cards. It is now the agreements
 * table, columns and footer identical, so a client rolls up exactly the way the
 * portfolio does and the two screens cannot disagree.
 */
function AgreementsTab({
  client,
  showArchived,
  onShowArchived,
}: {
  client: ClientDetail;
  showArchived: boolean;
  onShowArchived: (next: boolean) => void;
}) {
  const navigate = useNavigate();
  const all: AgreementTableRow[] = useMemo(
    () =>
      client.agreements.map((p) => ({
        id: p.id,
        name: p.name,
        code: p.code,
        type: p.type,
        isActive: p.isActive,
        hubProjectIds: p.hubProjectIds ?? [],
        metrics: p.metrics,
      })),
    [client.agreements],
  );
  const archivedCount = all.filter((p) => !p.isActive).length;
  // Open agreements by default; the footer totals what is on screen.
  const rows = useMemo(
    () => (showArchived ? all : all.filter((p) => p.isActive)),
    [all, showArchived],
  );
  const totals = useMemo(() => rollupAgreements(rows), [rows]);

  if (all.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<FileSignature />}
          title="No agreements yet"
          description={`Create an agreement for ${client.name} to start tracking time and budgets.`}
          action={
            <Link to="/agreements">
              <Button>
                <Plus className="h-4 w-4" aria-hidden />
                New agreement
              </Button>
            </Link>
          }
        />
      </Card>
    );
  }

  if (rows.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<FileSignature />}
          title="No open agreements"
          description={
            archivedCount === 1
              ? 'Its one agreement is archived.'
              : `All ${archivedCount} of its agreements are archived.`
          }
          action={
            <Button variant="outline" onClick={() => onShowArchived(true)}>
              <Archive className="h-4 w-4" aria-hidden />
              Show {archivedCount} archived
            </Button>
          }
        />
      </Card>
    );
  }

  return (
    <>
      {archivedCount > 0 && (
        <div className="mb-3 flex justify-end">
          <ArchivedToggle
            noun="agreements"
            active={showArchived}
            count={archivedCount}
            onChange={onShowArchived}
          />
        </div>
      )}
      <div className="space-y-2.5 md:hidden">
        {rows.map((p) => (
          <AgreementMobileCard key={p.id} agreement={p} href={agreementHref} />
        ))}
        <AgreementsTotalsCard totals={totals} />
      </div>
      <AgreementsTable
        className="hidden md:block"
        rows={rows}
        totals={totals}
        defaultSort={{ key: 'revenue', dir: 'desc' }}
        rowHref={agreementHref}
        onRowClick={(p) => navigate(agreementHref(p))}
      />
      <SupportAgreementNote client={client} />
    </>
  );
}

/**
 * A support agreement has no budget of its own — its hours draw on the client's
 * prepaid bank, which lives one tab away. Without this line the row for
 * "Meridian — Support" reads "No budget set · HEALTHY" on the very page whose
 * Support tab says the bank is 17 h in the red, and nothing on screen
 * reconciles the two.
 */
function SupportAgreementNote({ client }: { client: ClientDetail }) {
  const bank = client.support?.bank ?? null;
  if (!bank) return null;
  const over = bank.overage > 0;
  return (
    <p className="mt-3 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-body-sm text-grey-500">
      <LifeBuoy className="h-4 w-4 shrink-0 text-grey-400" aria-hidden />
      Support work carries no agreement budget — it draws on a prepaid bank:{' '}
      <span className={cn('num font-semibold', over ? 'text-status-late' : 'text-secondary')}>
        {over
          ? `${formatHours(bank.overage, { unit: true })} beyond it`
          : `${formatHours(bank.balance, { unit: true })} left`}
      </span>
      <Link
        to={`/clients/${client.id}?tab=support`}
        className="font-semibold text-accent hover:underline"
      >
        Support tab
      </Link>
    </p>
  );
}

// ---------------------------------------------------------------------------
// Invoices tab
// ---------------------------------------------------------------------------

function InvoicesTab({ client }: { client: ClientDetail }) {
  const navigate = useNavigate();
  const columns: Array<DataTableColumn<ClientInvoice>> = [
    {
      key: 'number',
      header: 'Invoice',
      sortable: true,
      sortValue: (i) => i.number,
      render: (i) => <span className="font-semibold text-secondary">#{i.number}</span>,
    },
    {
      key: 'issueDate',
      header: 'Issued',
      sortable: true,
      sortValue: (i) => i.issueDate,
      className: 'hidden sm:table-cell',
      render: (i) => formatDate(i.issueDate),
    },
    {
      key: 'dueDate',
      header: 'Due',
      sortable: true,
      sortValue: (i) => i.dueDate,
      className: 'hidden md:table-cell',
      render: (i) => (
        <span className={cn(i.isLate && 'font-semibold text-primary-600')}>
          {formatDate(i.dueDate)}
        </span>
      ),
    },
    {
      key: 'state',
      header: 'Status',
      render: (i) => (
        <StatusPill
          status={invoiceStatusToPill(i.state, i.isLate)}
          label={i.isLate ? 'Late' : undefined}
        />
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      sortable: true,
      sortValue: (i) => i.amount,
      render: (i) => formatMoney(i.amount),
    },
    {
      key: 'dueAmount',
      header: 'Balance',
      align: 'right',
      sortable: true,
      sortValue: (i) => i.dueAmount,
      className: 'hidden sm:table-cell',
      render: (i) =>
        i.state === 'VOID' || i.dueAmount <= 0 ? (
          <span className="text-grey-400">—</span>
        ) : (
          <span className={cn(i.isLate && 'font-semibold text-primary-600')}>
            {formatMoney(i.dueAmount)}
          </span>
        ),
    },
  ];

  return (
    <Card>
      <CardHeader
        title="Invoices"
        subtitle={`What ${client.name} has been billed, and what is still owed.`}
        actions={
          <Link to={newInvoiceHref(client.id)} className={buttonVariants({ size: 'sm' })}>
            <Plus className="h-4 w-4" aria-hidden />
            New invoice
          </Link>
        }
      />
      <DataTable
        columns={columns}
        data={client.invoices}
        rowKey={(i) => i.id}
        rowHref={(i) => withReturn(`/invoices/${i.id}`, 'client')}
        rowLabel={(i) => `Invoice ${i.number}`}
        onRowClick={(i) => navigate(withReturn(`/invoices/${i.id}`, 'client'))}
        empty={
          <EmptyState
            icon={<FileText />}
            title="No invoices yet"
            description={`Invoices you send to ${client.name} will show up here.`}
            action={
              <Link to={newInvoiceHref(client.id)}>
                <Button>
                  <Plus className="h-4 w-4" aria-hidden />
                  New invoice
                </Button>
              </Link>
            }
          />
        }
      />
      {client.stats.invoiceCount > client.invoices.length && (
        <div className="flex flex-wrap items-center justify-center gap-2 border-t border-grey-100 px-4 py-3 text-center">
          <span className="text-body-sm text-grey-500">
            The {client.invoices.length} most recent of {client.stats.invoiceCount}.
          </span>
          <Link
            to={`/invoices?client=${encodeURIComponent(client.id)}`}
            className="text-body-sm font-semibold text-accent hover:text-accent-700"
          >
            View all {client.stats.invoiceCount}
          </Link>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Support tab
// ---------------------------------------------------------------------------

/** "Support · 9.25 h" — the balance rides on the tab, so the one number that
 *  decides whether to open it is visible without opening it. */
function supportTabLabel(support: ClientSupport | null): string {
  const bank = support?.bank ?? null;
  if (!bank) return 'Support';
  if (bank.overage > 0) return `Support · ${formatHours(bank.overage, { unit: true })} over`;
  return `Support · ${formatHours(bank.balance, { unit: true })}`;
}

/**
 * Support, on the client page, is **the bank** — the same object `/support`
 * and the client portal read.
 *
 * This tab used to run the *other* model on its own: a monthly allowance meter
 * ("21.5 of 20 h · 108% of the monthly allowance used") and twelve rows of
 * allowance history, with no balance, no runway and no way to top up. The same
 * client, the same month, told two contradictory stories on two screens of one
 * app. The bank is now the only verdict here; the allowance survives as the
 * exception it is — named, dated, and explicitly not a second pool of hours.
 */
function SupportTab({ client }: { client: ClientDetail }) {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const support = client.support;
  const bank = support?.bank ?? null;
  const rate = support?.rate ?? null;

  // One target, prefilled: opening this from a client can never default to
  // somebody else's account. Closing it refreshes the page's own copy of the
  // bank as well as /support's.
  // Memoised on purpose — the modal re-seeds its form whenever this array's
  // identity changes, so a background refetch would otherwise wipe what the
  // user had typed.
  const topUpTargets = useMemo(
    () => [{ clientId: client.id, clientName: client.name, defaultRate: rate }],
    [client.id, client.name, rate],
  );
  const addHours = (
    <AddHoursModal
      open={addOpen}
      onOpenChange={(open) => {
        setAddOpen(open);
        if (!open) void queryClient.invalidateQueries({ queryKey: ['clients'] });
      }}
      clients={topUpTargets}
      initialClientId={client.id}
    />
  );

  if (!support) {
    return (
      <>
        <Card>
          <EmptyState
            icon={<LifeBuoy />}
            title="No support hours"
            description={`${client.name} has never bought a block of support hours. Buy one and it draws down as support time is logged — the balance, burn rate and run-out date appear here.`}
            action={
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button onClick={() => setAddOpen(true)}>
                  <Plus className="h-4 w-4" aria-hidden />
                  Add hours
                </Button>
                <Link to="/agreements">
                  <Button variant="outline">New support agreement</Button>
                </Link>
              </div>
            }
          />
        </Card>
        {addHours}
      </>
    );
  }

  return (
    <div className="space-y-4">
      {bank ? (
        <BankCard
          bank={bank}
          rate={rate}
          clientId={client.id}
          clientName={client.name}
          prepaidHours={client.stats.unbilledPrepaidHours}
          prepaidAmount={client.stats.unbilledPrepaid}
          unbilled={client.rollup.unbilled}
          unbilledExpenses={client.rollup.unbilledExpenses}
          unbilledFrom={client.stats.unbilledFrom}
          unbankedSales={support.unbankedSales ?? []}
          onAddHours={() => setAddOpen(true)}
        />
      ) : (
        <NoBankCard support={support} clientName={client.name} onAddHours={() => setAddOpen(true)} />
      )}

      {/* The blocks and the statement behind the balance — the SAME component
          /support expands under its own row, reading the same endpoint. Two
          copies is how the two screens drifted apart in the first place. */}
      {bank && (
        <Card padding="none">
          <div className="border-b border-grey-100 px-4 py-3 sm:px-6 sm:py-4">
            <h3 className="text-heading-4 text-secondary">Blocks and statement</h3>
            <p className="text-caption text-grey-500">
              What they bought, what is left of each block, and every movement behind the balance.
            </p>
          </div>
          <BankPanel clientId={client.id} showUnbanked={false} />
        </Card>
      )}

      <SupportHistoryCard support={support} hasBank={Boolean(bank)} />
      {addHours}
    </div>
  );
}

/** The bank itself: balance, what it is worth, how fast it is going, when it ends. */
function BankCard({
  bank,
  rate,
  clientId,
  clientName,
  prepaidHours,
  prepaidAmount,
  unbilled,
  unbilledExpenses,
  unbilledFrom,
  unbankedSales,
  onAddHours,
}: {
  bank: SupportBankRow;
  rate: number | null;
  clientId: string;
  clientName: string;
  /** Uninvoiced support work this bank has already paid for — the hours the
   *  Unbilled figure excludes. See ClientListRow#unbilled. */
  prepaidHours: number;
  prepaidAmount: number;
  /** Billable value with nothing behind it — on a support-only client, the
   *  overage. Net of this bank, which is what makes it safe to invoice, and
   *  inclusive of uninvoiced billable expenses, which is what makes it equal
   *  to what the wizard will draft. */
  unbilled: number;
  /** The expense half of `unbilled` — named, so the button cannot call an
   *  expense "overage". */
  unbilledExpenses: number;
  /** Oldest hour no block covered: the invoice wizard's window. */
  unbilledFrom: string | null;
  /** Hours sold on an invoice that never reached the bank — named beside the overage. */
  unbankedSales: UnbankedSale[];
  onAddHours: () => void;
}) {
  const overage = bank.overage > 0;
  // Hours already SOLD that never reached the bank. While they cover the
  // overage, the overage is not unpaid work — invoicing it would bill twice.
  const unbankedHours = unbankedSales.reduce((sum, s) => sum + s.hours, 0);
  const overageIsSold = overage && unbankedHours >= bank.overage;
  // Hours that die on a date the burn rate has no vote in. This is the case
  // that used to be invisible: the balance said 46% left, the pill said IN
  // CREDIT, and 27.75 paid-for hours were three weeks from evaporating.
  const expiring =
    bank.exhaustionReason === 'expiry' && bank.balance > 0 && bank.projectedExhaustion !== null;
  // Named apart from `bank.expiringSoon` (the per-BLOCK list) on purpose:
  // this one is the whole balance, and it is what the callout keys off.
  const expiryImminent = expiring && (bank.daysToExhaustion ?? 0) <= EXPIRY_HORIZON_DAYS;
  return (
    <Card padding="md">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="min-w-0">
          <p className="text-caption font-semibold uppercase tracking-wider text-grey-500">
            Support bank
          </p>
          {/* ⚠ HOURS ONLY. This figure carried "≈ $4,163" beside it — the
              balance × the block's rate, which is Σ(remaining hours × rate):
              the deferred-revenue computation Jesse ruled out ("that does not
              need to be logged anywhere or used in any of our decisions").
              The bank is an operational view of hours, and `/support` has
              always said it in hours. The Rate figure below still prices a
              SALE, which is a different question and is labelled as one. */}
          <p className="mt-1 text-heading-2 text-secondary num">
            {formatHours(bank.balance)}
            <span className="text-heading-4 text-grey-500"> h left</span>
          </p>
          <p className="mt-1 text-body-sm text-grey-500 num">
            Used {formatHours(bank.consumed)} of {formatHours(bank.purchased, { unit: true })}{' '}
            purchased
            {bank.pctRemaining !== null
              ? ` · ${formatPercent(bank.pctRemaining, { decimals: 0 })} left`
              : ''}
          </p>
        </div>
        <div className="flex flex-col items-start gap-1.5 sm:items-end">
          <BankStatusPill status={bank.status} />
          {bank.pendingHours > 0 && (
            <span className="num text-caption text-grey-500">
              +{formatHours(bank.pendingHours, { unit: true })} bought, awaiting payment
            </span>
          )}
          {/* Only when the callout below is NOT already saying it. A block
              expiring inside a bank whose runway ends on BURN is a genuinely
              separate fact and keeps its line; the same fact three times on one
              card is chrome. */}
          {!expiryImminent && bank.expiringSoon.length > 0 && (
            <span className="num text-caption font-semibold text-warning">
              {formatHours(bank.expiringSoon[0].remaining, { unit: true })} expiring{' '}
              {relativeDays(bank.expiringSoon[0].inDays)}
            </span>
          )}
        </div>
      </div>

      <div className="mt-4">
        <BalanceBar pct={bank.pctRemaining} status={bank.status} />
      </div>

      <UnpaidBankHours unpaid={bank.unpaidInvoices ?? []} className="mt-4" />

      {/* Pace and runway — the two figures a retainer owner asks for and
          Harvest does not compute. Same window, same wording as /support. */}
      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
        <div>
          <dt className="text-caption uppercase tracking-wider text-grey-500">Burn rate</dt>
          <dd className="num mt-0.5 text-body font-semibold text-secondary">
            {formatHours(bank.burnPerWeek, { unit: true })} / week
            <span className="block text-caption font-normal text-grey-500">
              over the last {Math.round(bank.burnWindowDays / 7)} weeks
            </span>
          </dd>
        </div>
        <div>
          <dt className="text-caption uppercase tracking-wider text-grey-500">Runs out</dt>
          <dd className="num mt-0.5 text-body font-semibold text-secondary">
            {bank.status === 'overage' || bank.status === 'exhausted' ? (
              <span className="text-status-late">out of hours</span>
            ) : bank.projectedExhaustion ? (
              <>
                {formatDate(bank.projectedExhaustion)}
                {/* WHICH CLOCK ENDED IT. The caption used to read "at this
                    pace" under EVERY date, including the ones the calendar
                    set. `projectedExhaustion` is the earlier of pace and
                    expiry, so on an expiring block that sentence credited a
                    2.29 h/week burn with emptying 27.75 h in 23 days.
                    `exhaustionReason` is the server saying which. */}
                {expiring ? (
                  <span
                    className="block text-caption font-normal text-warning"
                    title={
                      bank.paceExhaustion
                        ? `The hours expire on this date. At ${formatHours(bank.burnPerWeek)} h/week the balance itself would not have run out until ${formatDate(bank.paceExhaustion)}.`
                        : 'The hours expire on this date, whether or not they are burned.'
                    }
                  >
                    {relativeDays(bank.daysToExhaustion)} — they expire
                  </span>
                ) : (
                  <span className="block text-caption font-normal text-grey-500">
                    {relativeDays(bank.daysToExhaustion)} at this pace
                  </span>
                )}
              </>
            ) : (
              <span className="text-grey-400">—</span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-caption uppercase tracking-wider text-grey-500">Rate</dt>
          <dd className="num mt-0.5 text-body font-semibold text-secondary">
            {rate !== null ? (
              <>
                {formatMoney(rate)}/h
                <span className="block text-caption font-normal text-grey-500">
                  what they last bought at
                </span>
              </>
            ) : (
              <span className="text-grey-400">—</span>
            )}
          </dd>
        </div>
      </dl>

      {expiryImminent && (
        // The whole balance, in hours, with the date it goes and the date it
        // WOULD have gone. Two server dates side by side is the honest way to
        // show what is being given up without inventing a third number — and
        // pricing the balance would invent exactly the number the owner ruled
        // out (see the tile above).
        <p className="mt-4 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-body-sm text-secondary">
          <span className="num font-semibold text-warning">
            {formatHours(bank.balance, { unit: true })} expires{' '}
            {formatDate(bank.projectedExhaustion!)}
          </span>{' '}
          — {relativeDays(bank.daysToExhaustion)}.{' '}
          {bank.paceExhaustion
            ? `At ${formatHours(bank.burnPerWeek)} h/week ${clientName} would not have finished the balance until ${formatDate(bank.paceExhaustion)}, so the difference is paid-for time they simply lose.`
            : 'The hours go whether or not anyone burns them.'}{' '}
          Extend the block on the statement below, or send them their link so they can use it.
        </p>
      )}

      {overage && (
        // Hours in dollars: "how much are we eating this month" was one
        // multiplication away and nowhere on the screen.
        <p className="mt-4 rounded-lg border border-status-late-border bg-status-late-bg px-3 py-2 text-body-sm text-secondary">
          <span className="num font-semibold text-status-late">
            {formatHours(bank.overage, { unit: true })} beyond the bank
            {rate !== null ? ` · ${formatMoney(bank.overage * rate, { whole: true })}` : ''}
          </span>{' '}
          {unbankedHours > 0
            ? '— the bank is missing hours already sold on an invoice (below).'
            : `— support delivered to ${clientName} with no purchased hours behind it. Top up the bank, or invoice it below.`}
        </p>
      )}

      <UnbankedSales clientId={clientId} sales={unbankedSales} overage={bank.overage} />

      {prepaidHours > 0 && (
        // ⚠ THE OTHER HALF OF THE OVERAGE SENTENCE. A block is prepayment or it
        // is not; this app says it is, and printed its purchase value and a
        // Refund button beside it. So the hours it covered are PAID, and the
        // invoicing side must never offer them again. Saying so here, next to
        // the balance the money bought, is what keeps the two readings of the
        // same hours from drifting apart.
        <p className="mt-4 rounded-lg border border-grey-200 bg-grey-50 px-3 py-2 text-body-sm text-grey-600">
          <span className="num font-semibold text-secondary">
            {formatHours(prepaidHours, { unit: true })}
            {prepaidAmount > 0 ? ` · ${formatMoney(prepaidAmount, { whole: true })}` : ''}
          </span>{' '}
          of uninvoiced support work has already been paid for out of this bank. It is excluded from
          every unbilled figure for {clientName} — and the invoice wizard will not compose a line
          from it either: <Link to="/invoices/new" className="font-semibold underline">Ready to
          bill</Link> nets this same bank off before it prices anything. Invoicing it would charge
          twice for the same hour.
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {/* ⚠ THE OVERAGE HAS A WAY OUT NOW. The app computed the exact figure —
            Meridian, 17 h beyond every block, itemised into 12 ledger rows —
            and offered nothing to do with it: the only invoice path was the
            wizard, and the wizard offered every uninvoiced support hour,
            prepaid ones included. It offers the net figure now, so the card
            that raises the alarm can settle it in one click. `unbilled` is the
            server's bank-net number, so this button and that draft agree. */}
        {overage && !overageIsSold && unbilled > 0 && (
          <Link
            to={
              `/invoices/new?tab=unbilled&clientId=${encodeURIComponent(clientId)}` +
              (unbilledFrom ? `&from=${unbilledFrom}&to=${new Date().toLocaleDateString('en-CA')}` : '')
            }
            className={buttonVariants({ size: 'sm' })}
            title={
              `Open the invoice wizard on the ${formatHours(bank.overage, {
                unit: true,
              })} this bank did not cover. The prepaid hours are excluded — the draft never bills an hour twice` +
              (unbilledExpenses > 0
                ? `, and it also carries ${formatMoney(unbilledExpenses, {
                    whole: true,
                  })} of billable expenses nobody has invoiced.`
                : '.')
            }
          >
            <Receipt className="h-4 w-4" aria-hidden />
            {/* ⚠ "overage" only when the figure IS the overage. Once billable
                expenses ride in the same total, calling it overage names one
                part of it and mis-describes the rest. */}
            Invoice {formatMoney(unbilled, { whole: true })}
            {unbilledExpenses > 0 ? ' unbilled' : ' overage'}
          </Link>
        )}
        <Button size="sm" variant={overage && unbilled > 0 ? 'outline' : 'primary'} onClick={onAddHours}>
          <Plus className="h-4 w-4" aria-hidden />
          Add hours
        </Button>
        {/* The alert and the way to act on it, on one screen. Before this the
            only route to a client's top-up link was Settings -> Client portal
            -> find them in a list -> Create link -> copy -> open a mail
            client: four navigations to answer an alarm this card just rang. */}
        <SendTopUpLink clientId={clientId} clientName={clientName} />
        <Link to="/support" className={buttonVariants({ variant: 'outline', size: 'sm' })}>
          Support report
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>
    </Card>
  );
}

/**
 * A support client who has never bought hours. That state used to render as a
 * dash where a balance should be, next to a monthly allowance nobody explained
 * — the "impossible half-state". It is now named, and it has the one action
 * that resolves it.
 */
function NoBankCard({
  support,
  clientName,
  onAddHours,
}: {
  support: ClientSupport;
  clientName: string;
  onAddHours: () => void;
}) {
  const { allowance, currentMonth } = support;
  return (
    <Card padding="md">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-caption font-semibold uppercase tracking-wider text-grey-500">
            Support bank
          </p>
          <p className="mt-1 text-heading-4 text-secondary">No hours bought</p>
          <p className="mt-1 max-w-xl text-body-sm text-grey-500">
            {allowance
              ? `${clientName} is on a monthly allowance of ${formatHours(allowance.monthlyHours, {
                  unit: true,
                })}, in force since ${formatDate(allowance.effectiveFrom)} — an entitlement that resets each month, not a balance that carries over. Buy a block of hours to give them a bank that does.`
              : `${clientName} has support work but has never bought a block of hours, so there is no balance to draw down.`}
          </p>
        </div>
        <BankStatusPill status={null} />
      </div>

      {allowance && currentMonth && (
        <div className="mt-4">
          <div className="flex items-baseline justify-between gap-3">
            <p className="num text-body-sm font-semibold text-secondary">
              {formatMonth(currentMonth.month)} · {formatHours(currentMonth.used)} of{' '}
              {formatHours(allowance.monthlyHours, { unit: true })}
              {currentMonth.value !== null && (
                <span className="ml-2 font-normal text-grey-500">
                  {formatMoney(currentMonth.value, { whole: true })} of work
                </span>
              )}
            </p>
            {currentMonth.overage && currentMonth.overage > 0 ? (
              <StatusPill
                status="error"
                label={`${formatHours(currentMonth.overage, { unit: true })} over`}
              />
            ) : (
              <StatusPill
                status={(currentMonth.pct ?? 0) >= 0.9 ? 'warning' : 'success'}
                label={`${formatHours(currentMonth.remaining ?? 0, { unit: true })} left this month`}
              />
            )}
          </div>
          {currentMonth.pct !== null && <ProgressBar value={currentMonth.pct} className="mt-2" />}
        </div>
      )}

      <div className="mt-4">
        <Button size="sm" onClick={onAddHours}>
          <Plus className="h-4 w-4" aria-hidden />
          Add hours
        </Button>
      </div>
    </Card>
  );
}

/**
 * Support hours month by month — description, never a second verdict.
 *
 * Where a bank exists this prints hours and what they were worth; the allowance
 * columns appear only for a client whose allowance is the model actually in
 * force. Two models both grading the same hours is the contradiction this whole
 * tab was rebuilt to remove.
 */
function SupportHistoryCard({ support, hasBank }: { support: ClientSupport; hasBank: boolean }) {
  const { history, allowance, rate } = support;

  const columns: Array<DataTableColumn<SupportMonth>> = [
    {
      key: 'month',
      header: 'Month',
      render: (m) => <span className="font-semibold text-secondary">{formatMonth(m.month)}</span>,
    },
    {
      key: 'used',
      header: 'Hours',
      align: 'right',
      render: (m) => formatHours(m.used, { unit: true, fixed: true }),
    },
    {
      key: 'value',
      header: (
        <span title={rate !== null ? `Hours × ${formatMoney(rate)}/h` : 'No support rate on record'}>
          Value
        </span>
      ),
      align: 'right',
      className: 'hidden sm:table-cell',
      render: (m) =>
        m.value !== null ? (
          formatMoney(m.value, { whole: true })
        ) : (
          <span className="text-grey-400">—</span>
        ),
    },
    ...(hasBank
      ? []
      : [
          {
            key: 'allowance',
            header: 'Allowance',
            align: 'right' as const,
            className: 'hidden sm:table-cell',
            render: (m: SupportMonth) =>
              m.allowance !== null ? (
                formatHours(m.allowance, { unit: true })
              ) : (
                <span className="text-grey-400">—</span>
              ),
          },
          {
            key: 'pct',
            header: '% used',
            align: 'right' as const,
            className: 'w-40',
            render: (m: SupportMonth) =>
              m.pct !== null ? (
                <div className="flex items-center justify-end gap-2">
                  <ProgressBar value={m.pct} size="sm" className="w-16" />
                  <span
                    className={cn(
                      'w-12 font-semibold num',
                      m.pct >= 1 ? 'text-error' : m.pct >= 0.9 ? 'text-warning' : 'text-secondary-800',
                    )}
                  >
                    {formatPercent(m.pct, { decimals: 0 })}
                  </span>
                </div>
              ) : (
                <span className="text-grey-400">—</span>
              ),
          },
        ]),
  ];

  const total = history.reduce((s, m) => s + m.used, 0);
  const totalValue = history.reduce((s, m) => s + (m.value ?? 0), 0);

  return (
    <Card padding="none">
      <div className="border-b border-grey-100 px-4 py-3 sm:px-6 sm:py-4">
        <h3 className="text-heading-4 text-secondary">Support hours by month</h3>
        <p className="text-caption text-grey-500">
          Billable hours logged on this client’s support work
          {rate !== null ? `, valued at ${formatMoney(rate)}/h` : ''}.
          {hasBank && allowance && (
            <>
              {' '}
              They also carry a {formatHours(allowance.monthlyHours, { unit: true })}/month allowance
              recorded since {formatDate(allowance.effectiveFrom)} — the hours themselves draw on the
              bank above, so it is not a second pool.
            </>
          )}
        </p>
      </div>
      <DataTable
        columns={columns}
        data={history}
        rowKey={(m) => m.month}
        footer={
          history.length > 0 ? (
            <tr>
              <td className="px-4 py-2.5 text-caption uppercase tracking-wider text-grey-500">
                {history.length} month{history.length === 1 ? '' : 's'}
              </td>
              <td className="num-cell px-4 py-2.5">{formatHours(total, { unit: true, fixed: true })}</td>
              <td className="num-cell hidden px-4 py-2.5 sm:table-cell">
                {rate !== null ? formatMoney(totalValue, { whole: true }) : ''}
              </td>
              {!hasBank && <td className="hidden sm:table-cell" />}
              {!hasBank && <td />}
            </tr>
          ) : undefined
        }
        empty={
          <EmptyState
            icon={<LifeBuoy />}
            title="No support hours yet"
            description="Support time tracked for this client will appear here month by month."
          />
        }
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Notes tab
// ---------------------------------------------------------------------------

function NotesTab({ client, onEdit }: { client: ClientDetail; onEdit: () => void }) {
  if (!client.notes) {
    return (
      <Card>
        <EmptyState
          icon={<StickyNote />}
          title="No notes yet"
          description="Keep billing contacts, terms, and context for your team here."
          action={
            <Button variant="outline" onClick={onEdit}>
              <Pencil className="h-4 w-4" aria-hidden />
              Add notes
            </Button>
          }
        />
      </Card>
    );
  }
  return (
    <Card padding="md">
      <div className="flex items-start justify-between gap-4">
        <p className="whitespace-pre-wrap text-body-sm leading-relaxed text-secondary-800">
          {client.notes}
        </p>
        <Button variant="ghost" size="sm" onClick={onEdit} className="shrink-0">
          <Pencil className="h-4 w-4" aria-hidden />
          Edit
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function DetailSkeleton() {
  return (
    <div aria-busy>
      <Skeleton className="mb-3 h-4 w-20" />
      <div className="mb-2 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-6 w-24 rounded-full" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-40" />
          <Skeleton className="h-9 w-20" />
        </div>
      </div>
      <Skeleton className="mb-4 h-4 w-96 max-w-full" />
      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-28" />
        ))}
      </div>
      <Skeleton className="mb-4 h-10 w-96 max-w-full" />
      <Skeleton className="h-64" />
    </div>
  );
}
